import type { FC } from 'react';
import React, { useState } from 'react';

import type { IconName, PictogramNameType } from '@klp/design/Illustrations';
import { Illustration } from '@klp/design/Illustrations';
import {
    Collapse,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import type { SidebarMenuItem } from '../classes/SidebarMenuItem';

import { ExpandLess, ExpandMore } from '@material-ui/icons';

import { NestedSidebarMenuSubitem } from './NestedSidebarMenuSubitem';
import { useAppParams } from '@forvalter/packages/appParams/useAppParams';
import { useCurrentUser } from '@klp/auth';

type NestedSidebarItemProps = {
    icon?: IconName;
    pictogram?: PictogramNameType;
    translation: string;
    nestedItems: Array<SidebarMenuItem>;
};

export const NestedSidebarMenuItem: FC<NestedSidebarItemProps> = ({
    icon,
    pictogram,
    translation,
    nestedItems,
}) => {
    const { t } = useTranslation();
    const { matchRoute } = useAppParams();
    const { currentUser } = useCurrentUser();
    const visibleNestedItems = nestedItems?.filter((item) => {
        if (item?.access && !item?.access?.(currentUser)) {
            return false;
        }
        if (item?.condition && !item?.condition?.(item)) {
            return false;
        }
        return true;
    });

    const match = visibleNestedItems?.some((element) =>
        matchRoute(element.matchRoute || ''),
    );

    const [open, setOpen] = useState(match);

    if (visibleNestedItems?.length === 0) {
        return null;
    }

    return (
        <>
            <ListItem button={true} onClick={() => setOpen(!open)}>
                <ListItemIcon>
                    {pictogram && <Illustration pictogram={pictogram} />}
                    {icon && (
                        <Illustration
                            icon={icon}
                            klpColor="veryLight"
                            klpPalette="primary"
                        />
                    )}
                </ListItemIcon>
                <ListItemText primary={t(translation || '')} />
                {open ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse
                in={open}
                timeout="auto"
                unmountOnExit={true}
                className="Sidebar__MenuListCollapse"
            >
                <List component="nav" className="Sidebar__MenuList--nested">
                    {visibleNestedItems?.map((element, key) => (
                        <NestedSidebarMenuSubitem key={key} element={element} />
                    ))}
                </List>
            </Collapse>
        </>
    );
};
